/**
 * Infinite Loop Slider
 * Vanilla JS implementation optimized for 60fps
 * Uses transform-based sliding and node cloning for the endless loop effect.
 */

    if (typeof window.InfiniteSlider === 'undefined') {

class InfiniteSlider {
  constructor(element) {
    this.section = element;
    this.viewport = this.section.querySelector('[data-slide-viewport]');
    this.track = this.section.querySelector('[data-slide-track]');
    this.prevBtn = this.section.querySelector('.cig-prev');
    this.nextBtn = this.section.querySelector('.cig-next');

    // On mobile (<= 768px) we use native CSS scroll-snap - no JS slider needed.
    this.isMobile = window.innerWidth <= 768;

    // Vertical grid mode: skip slider, only run card reveal animation
    this.isVertical = this.section.dataset.layout === 'vertical';
    if (this.isVertical) {
      this.items = Array.from(this.track.querySelectorAll('[data-slide-item]'));
      this.allTrackItems = this.items;
      this.initRevealObserver();
      return;
    }
    
    this.items = Array.from(this.track.querySelectorAll('[data-slide-item]'));
    this.itemCount = this.items.length;
    
    if (this.itemCount === 0) return;

    this.currentIndex = 0;
    this.isAnimating = false;
    this.cardsVisible = this.getVisibleCards();
    
    // Autoplay configuration
    this.autoplayInterval = parseInt(this.section.dataset.autoplay) || 0;
    this.autoplayTimer = null;
    this.isHoveringOrDragging = false;
    
    this.init();
    this.initRevealObserver();
    this.initMagneticButtons();
    this.startAutoplay();
  }

  startAutoplay() {
    if (this.isMobile) return;
    if (this.autoplayInterval > 0 && !this.isHoveringOrDragging) {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.isSectionInView || document.hidden) return;
        this.slide('next');
      }, this.autoplayInterval * 1000);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  getVisibleCards() {
    if (window.innerWidth <= 480) {
      return parseInt(this.section.dataset.colsMobile) || 1;
    }
    if (window.innerWidth <= 768) return 2;
    if (window.innerWidth <= 1024) return 3;
    return 4;
  }

  init() {
    // On mobile, CSS scroll-snap handles all touch interaction.
    // We only need to set up nav buttons and resize handling.
    if (this.isMobile) {
      // Mark clones (added by cloneItems below) - CSS hides them on mobile.
      // Skip cloning entirely on mobile since we use native scroll.
      this.allTrackItems = Array.from(this.track.querySelectorAll('[data-slide-item]'));
      this.clonesOffset = 0;
      this.itemWidth = 0;
      this.setupMobileArrows();
      return;
    }

    // Clone items to create the illusion of infinite scroll
    this.cloneItems();
    
    // Setup initial position
    this.updateDimensions();
    this.setPosition(this.currentIndex, false);

    // Event Listeners
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.slide('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.slide('next'));
    }

    // Handle Resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;
        const newVisible = this.getVisibleCards();
        if (newVisible !== this.cardsVisible) {
          this.cardsVisible = newVisible;
        }
        if (!this.isMobile) {
          this.updateDimensions();
          this.setPosition(this.currentIndex, false);
        }
      }, 150);
    });

    // Drag/swipe implementation (pointer events) - desktop only
    this.setupTouch();
    
    // Desktop Navigation
    this.setupKeyboard();
    this.setupWheel();
  }

  cloneItems() {
    // Determine how many items to clone. We clone enough to cover a full view plus margin.
    const numToClone = this.cardsVisible;
    
    const firstClones = this.items.slice(0, numToClone).map(i => {
      const clone = i.cloneNode(true);
      clone.classList.add('cig-clone');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    });

    const lastClones = this.items.slice(-numToClone).map(i => {
      const clone = i.cloneNode(true);
      clone.classList.add('cig-clone', 'is-in-view');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    });

    // Prepend last clones in correct order
    lastClones.reverse().forEach(clone => this.track.prepend(clone));
    
    // Append first clones
    firstClones.forEach(clone => this.track.appendChild(clone));

    this.allTrackItems = Array.from(this.track.children);
    this.clonesOffset = numToClone;
  }

  updateDimensions() {
    // Get width of a single item including gap
    if (this.allTrackItems.length === 0) return;
    
    const itemContent = this.items[0];
    const width = itemContent.getBoundingClientRect().width;
    
    // If not painted yet, defer calculation
    if (width === 0) {
      requestAnimationFrame(() => this.updateDimensions());
      return;
    }
    
    // Gap computation: gap is usually pulled via computerStyle
    const trackStyle = window.getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.gap) || 0;
    
    this.itemWidth = width + gap;
  }

  setPosition(index, animate = true) {
    // Transform index into the actual DOM index considering clones
    const actualIndex = index + this.clonesOffset;
    const offset = -(actualIndex * this.itemWidth);

    if (animate) {
      // High-end spring-like cubic bezier transition
      this.track.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
    } else {
      // Instant snap
      this.track.style.transition = 'none';
    }

    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
  }

  slide(direction) {
    if (this.isAnimating) return;
    this.isAnimating = true;

    if (direction === 'next') {
      this.currentIndex += this.cardsVisible;
    } else {
      this.currentIndex -= this.cardsVisible;
    }

    this.setPosition(this.currentIndex, true);

    // Sequence wrap-around
    setTimeout(() => {
      if (this.currentIndex >= this.itemCount) {
        // Scrolled past the end of real items, snap back to start
        this.currentIndex = this.currentIndex % this.itemCount;
        this.setPosition(this.currentIndex, false);
      } else if (this.currentIndex < 0) {
        // Scrolled past the beginning, snap back to end
        // Modulo handles negative wrap: -4 % 12 = -4. We add itemCount.
        this.currentIndex = (this.currentIndex % this.itemCount) + this.itemCount;
        if (this.currentIndex === this.itemCount) this.currentIndex = 0;
        this.setPosition(this.currentIndex, false);
      }
      this.isAnimating = false;
    }, 550); // Wait for transition to finish + 50ms buffer
  }

  setupTouch() {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    let directionLocked = null; // 'horizontal', 'vertical', or null
    let lastX = 0;
    let lastTime = 0;
    let recentVelocity = 0;
    let rafId = null;
    let startTarget = null;

    this.track.addEventListener('pointerdown', (e) => {
      if (this.isAnimating) return;
      if (e.target.closest('button') || e.target.closest('.cosmic-quick-add')) return;

      if (rafId) cancelAnimationFrame(rafId);
      
      isDragging = true;
      directionLocked = null;
      currentX = 0;
      startX = e.clientX;
      startY = e.clientY;
      lastX = e.clientX;
      lastTime = Date.now();
      recentVelocity = 0;
      startTarget = e.target;
      
      this.isHoveringOrDragging = true;
    });

    this.track.addEventListener('pointermove', (e) => {
      if (!isDragging) return;

      const diffX = e.clientX - startX;
      const diffY = e.clientY - startY;

      // Intent Detection Phase
      if (directionLocked === null) {
        const totalDist = Math.sqrt(diffX * diffX + diffY * diffY);
        if (totalDist < 6) return; // Dead zone

        if (Math.abs(diffX) > Math.abs(diffY)) {
          directionLocked = 'horizontal';
          this.stopAutoplay();
          this.track.style.transition = 'none';
          this.track.setPointerCapture(e.pointerId);
        } else {
          directionLocked = 'vertical';
          isDragging = false;
          return;
        }
      }

      if (directionLocked === 'vertical') return;

      currentX = diffX;
      
      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) {
        recentVelocity = (e.clientX - lastX) / dt;
        lastX = e.clientX;
        lastTime = now;
      }
      
      const actualIndex = this.currentIndex + this.clonesOffset;
      const baseOffset = -(actualIndex * this.itemWidth);
      
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        this.track.style.transform = `translate3d(${baseOffset + currentX}px, 0, 0)`;
      });
    }, { passive: true });

    const endDrag = (e) => {
      if (!isDragging && directionLocked !== 'horizontal') {
        this.isHoveringOrDragging = false;
        this.startAutoplay();
        return;
      }
      
      const wasHorizontal = directionLocked === 'horizontal';
      isDragging = false;
      directionLocked = null;
      
      if (wasHorizontal) {
        this.track.releasePointerCapture(e.pointerId);
      }
      
      if (rafId) cancelAnimationFrame(rafId);

      const threshold = this.itemWidth * 0.15;
      const velocityThreshold = 0.4;
      
      const flickingNext = recentVelocity < -velocityThreshold;
      const flickingPrev = recentVelocity > velocityThreshold;
      
      const draggedFarNext = currentX < -threshold;
      const draggedFarPrev = currentX > threshold;

      if (wasHorizontal && (flickingNext || draggedFarNext)) {
        this.slide('next');
      } else if (wasHorizontal && (flickingPrev || draggedFarPrev)) {
        this.slide('prev');
      } else {
        this.setPosition(this.currentIndex, true);
      }

      currentX = 0;
      this.isHoveringOrDragging = false;
      this.startAutoplay();
    };

    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointercancel', endDrag);

    this.track.addEventListener('click', (e) => {
      // If we dragged significantly, block the click event to prevent accidental navigation
      if (Math.abs(currentX) > 10) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true });
  }

  setupKeyboard() {
    let focusObserver = new IntersectionObserver((entries) => {
      this.isSectionInView = entries[0].isIntersecting;
    }, { threshold: 0.5 });
    
    focusObserver.observe(this.section);

    document.addEventListener('keydown', (e) => {
      // Don't trigger if user is typing
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Handle Quick Add Modal Mockup Navigation robustly
      const isModalActive = document.body.classList.contains('overflow-hidden') || document.querySelector('quick-add-modal[open], dialog[open]');
      if (isModalActive) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextBtns = document.querySelectorAll('.cqa-arrow.next');
          const activeNext = Array.from(nextBtns).find(b => b.offsetParent !== null);
          if (activeNext) activeNext.click();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevBtns = document.querySelectorAll('.cqa-arrow.prev');
          const activePrev = Array.from(prevBtns).find(b => b.offsetParent !== null);
          if (activePrev) activePrev.click();
        }
        return; // Prevent background slider from fundamentally changing
      }

      // Only trigger grid slider if section occupies at least 50% of screen
      if (!this.isSectionInView || this.isAnimating) return;
      
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.slide('next');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.slide('prev');
      }
    });
  }

  setupWheel() {
    let isWheeling = false;
    
    // Pause autoplay on mouse hover anywhere over the section
    this.section.addEventListener('mouseenter', () => {
      this.isHoveringOrDragging = true;
      this.stopAutoplay();
    });
    this.section.addEventListener('mouseleave', () => {
      this.isHoveringOrDragging = false;
      this.startAutoplay();
    });

    this.viewport.addEventListener('wheel', (e) => {
      // Prioritize horizontal scrolls
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        
        if (this.isAnimating || isWheeling) return;
        
        // Trigger slide on the first solid trackpad movement
        if (Math.abs(e.deltaX) > 15) {
          isWheeling = true;
          
          if (e.deltaX > 0) {
            this.slide('next');
          } else {
            this.slide('prev');
          }
          
          // Block momentum scroll events from triggering another slide immediately
          setTimeout(() => {
            isWheeling = false;
          }, 800);
        }
      }
    }, { passive: false });
  }

  initRevealObserver() {
    const options = {
      root: null,
      threshold: 0.1,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('is-in-view');
          }, index * 50);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    // Only observe REAL items, not clones, to avoid flash/pop on wrap-around
    const items = this.isVertical ? this.allTrackItems : this.items;
    if (items) {
      items.forEach(item => observer.observe(item));
    }
  }

  initMagneticButtons() {
    const magneticElements = this.section.querySelectorAll('.cig-nav-btn, .cig-view-all-btn');
    
    magneticElements.forEach(btn => {
      // Create a wrapper/element specific transition for smooth return
      btn.style.transition = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s cubic-bezier(0.22, 1, 0.36, 1)';
      
      const icon = btn.querySelector('.cig-icon, .cig-icon-arrow');
      if (icon) {
        icon.style.transition = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)';
      }

      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        // Remove transition during movement for immediate tracking (or keep a very fast one, but none is usually best for magnetic)
        btn.style.transition = 'none';
        btn.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`;
        
        if (icon) {
          icon.style.transition = 'none';
          icon.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
        }
      });
      
      btn.addEventListener('mouseleave', () => {
        // Restore transition for snap back
        btn.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s ease, border-color 0.2s ease';
        btn.style.transform = 'translate(0px, 0px)';
        
        if (icon) {
          icon.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
          icon.style.transform = 'translate(0px, 0px)';
        }
      });
    });
  }

  setupMobileArrows() {
    const prevBtn = this.section.querySelector('.cig-mobile-prev');
    const nextBtn = this.section.querySelector('.cig-mobile-next');
    if (!prevBtn || !nextBtn) return;

    const viewport = this.viewport;

    // Get card scroll width dynamically
    const getScrollAmount = () => {
      const card = this.track.querySelector('.cig-card:not(.cig-clone)');
      if (!card) return viewport.clientWidth * 0.5;
      const style = window.getComputedStyle(this.track);
      const gap = parseFloat(style.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    // Scroll forward
    nextBtn.addEventListener('click', () => {
      viewport.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
    });

    // Scroll back
    prevBtn.addEventListener('click', () => {
      viewport.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
    });

    // Show/hide arrows based on scroll position
    const updateArrows = () => {
      if (viewport.scrollLeft > 20) {
        prevBtn.classList.add('is-visible');
      } else {
        prevBtn.classList.remove('is-visible');
      }

      // Show next arrow if there is more content to the right
      const atEnd = Math.ceil(viewport.scrollLeft + viewport.clientWidth) >= viewport.scrollWidth - 5;
      if (!atEnd) {
        nextBtn.classList.add('is-visible');
      } else {
        nextBtn.classList.remove('is-visible');
      }
    };

    viewport.addEventListener('scroll', updateArrows, { passive: true });
    // Run once on init so next arrow is shown at page load
    requestAnimationFrame(updateArrows);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.cosmic-infinite-grid-section');
  sections.forEach(s => new InfiniteSlider(s));
});

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.classList.contains('cosmic-infinite-grid-section') || 
        e.target.querySelector('.cosmic-infinite-grid-section')) {
      const s = e.target.querySelector('.cosmic-infinite-grid-section') || e.target;
      new InfiniteSlider(s);
    }
  });
}

window.InfiniteSlider = InfiniteSlider;
}
